import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import { getDvSourceImageUrl, themeBackgroundKeyForViewport } from "../../functions/themeAssets";

// Original Workplace Triumph creative (old/index.php): office hallway background
// and the animated "Workplace Triumph" title gif. Used whenever the active theme
// hasn't got its own background/logo configured, so the login screen never renders blank.
const FALLBACK_BG = `${process.env.PUBLIC_URL}/assets/login/bg.jpg`;
const FALLBACK_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/login/index_mob.png`;
const FALLBACK_LOGO_GIF = `${process.env.PUBLIC_URL}/assets/login/logo.gif`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { data: themeData } = useSelector((state) => state.theme);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const heroRef = useRef(null);
  const [backgroundKey, setBackgroundKey] = useState(null);
  const [isMobilePortrait, setIsMobilePortrait] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_PORTRAIT_MQ).matches
      : false
  );

  useEffect(() => {
    // iOS/WebKit bug: a `position: fixed` element (.login-hero) can keep a
    // stale, often much-too-small layout height instead of recomputing
    // against the current viewport — leaving most of the screen blank/white
    // below whatever sliver did get sized. This isn't only triggered by
    // rotation: it reliably shows up right after the OTP keyboard dismisses
    // on login success, because the keyboard hide is itself a viewport
    // resize that WebKit fails to re-layout fixed elements for. Forcing a
    // reflow (toggle display off/on) works around it, but it has to run
    // once the stale height is actually baked in, not just on every future
    // resize/orientationchange — otherwise a device that never rotates
    // stays stuck on the too-small height forever.
    const forceReflow = () => {
      const el = heroRef.current;
      if (!el) return;
      el.style.display = "none";
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      el.style.display = "";
    };

    // Run once after mount/paint to correct any stale height already baked
    // in from before this component existed (e.g. the OTP keyboard closing
    // on the previous screen, just before navigating here). A second,
    // delayed run covers the case where iOS hasn't finished settling its
    // internal viewport metrics by the first animation frame — the keyboard
    // dismiss animation itself takes a couple hundred ms.
    const raf = requestAnimationFrame(forceReflow);
    const timeout = setTimeout(forceReflow, 350);

    window.addEventListener("orientationchange", forceReflow);
    window.addEventListener("resize", forceReflow);
    // iOS fires viewport-only resizes (e.g. keyboard show/hide) through
    // visualViewport, not always through window's resize event.
    window.visualViewport?.addEventListener("resize", forceReflow);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("orientationchange", forceReflow);
      window.removeEventListener("resize", forceReflow);
      window.visualViewport?.removeEventListener("resize", forceReflow);
    };
  }, []);

  // Themed hero background (desktop vs mobile art), same selection rule used
  // for stage/landing backgrounds elsewhere (see themeBackgroundKeyForViewport).
  useEffect(() => {
    const applyBackgroundKey = () => {
      setBackgroundKey(themeBackgroundKeyForViewport(themeData));
      setIsMobilePortrait(window.matchMedia(MOBILE_PORTRAIT_MQ).matches);
    };
    applyBackgroundKey();
    window.addEventListener("resize", applyBackgroundKey);
    window.addEventListener("orientationchange", applyBackgroundKey);
    return () => {
      window.removeEventListener("resize", applyBackgroundKey);
      window.removeEventListener("orientationchange", applyBackgroundKey);
    };
  }, [themeData]);

  const backgroundUrl = useMemo(
    () =>
      (backgroundKey ? getDvSourceImageUrl(backgroundKey) : null) ||
      (isMobilePortrait ? FALLBACK_BG_MOBILE : FALLBACK_BG),
    [backgroundKey, isMobilePortrait]
  );

  const titleLogoUrl = useMemo(
    () => (themeData?.logo ? getDvSourceImageUrl(themeData.logo) : null) || FALLBACK_LOGO_GIF,
    [themeData]
  );

  const titleText = useMemo(() => {
    const t = themeData?.landing_page_title || themeData?.themename || "";
    return String(t).trim();
  }, [themeData]);

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;
      setBeginLoading(true);
      try {
        navigate("/intro", { replace: true });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    // The hero background lives on .login-hero itself (see backgroundUrl above),
    // not on the shared body background used by other pages — clear any of that
    // out here and just lock page scroll behind the full-bleed hero.
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    document.body.classList.remove("common-bg");
    document.body.style.backgroundImage = "none";
    document.body.style.background = "none";
    document.body.style.backgroundAttachment = "initial";
    document.body.style.backgroundPosition = "initial";
    document.body.style.backgroundSize = "initial";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    Swal.fire("You have already played!", "  ", "error").then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  return (
    <div
      className="login-hero"
      ref={heroRef}
      style={backgroundUrl ? { backgroundImage: `url("${backgroundUrl}")` } : undefined}
    >
      <div className="login-title-wrap">
        <img src={titleLogoUrl} alt={titleText || "Workplace Triumph"} className="login-title-logo" />
      </div>

      <div className="login-overlay">
        {!isAdmin ? (
          <button
            type="button"
            className="begin-play-btn"
            disabled={beginLoading || status !== "succeeded" || !user}
            onClick={handleBeginPlay}
          >
            {beginLoading ? "LOADING…" : "BEGIN PLAY"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default Login;
